import React, { useState } from 'react';
import ModalPortal from './ModalPortal';
import MenuButton from './MenuButton';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim() || '';

function AuthModal({ isOpen, onClose, onLogin }) {
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    setMode('login');
    onClose();
  };

  const switchMode = () => {
    setError('');
    setMode(mode === 'login' ? 'register' : 'login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (username.length < 3) {
          setError('Username must be at least 3 characters');
          setLoading(false);
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: username.toLowerCase().trim(),
            password
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.error || 'Registration failed');
          setLoading(false);
          return;
        }

        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        onLogin(data.user, data.token);
        handleClose();
      } else {
        const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: username.trim().toLowerCase(),
            password
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.error || 'Login failed');
          setLoading(false);
          return;
        }

        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        onLogin(data.user, data.token);
        handleClose();
      }
    } catch (err) {
      setError('Connection error. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div className="settings-modal-overlay" onClick={handleClose}>
        <div className="settings-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
          <div className="settings-modal-header">
            <h3>{mode === 'login' ? 'Login' : 'Create Account'}</h3>
            <button className="icon-button" onClick={handleClose} title="Close">✖</button>
          </div>

          {error && (
            <div style={{ 
              background: 'rgba(255,68,68,0.15)', 
              border: '1px solid rgba(255,68,68,0.3)', 
              borderRadius: 8, 
              padding: '0.75rem', 
              marginBottom: '1rem',
              color: '#ffb4b4'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="control-group" style={{ marginBottom: '1rem' }}>
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={mode === 'login' ? 'Enter username' : 'Choose a username'}
                required
                autoFocus
              />
            </div>

            <div className="control-group" style={{ marginBottom: '1rem' }}>
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'login' ? 'Enter password' : 'Create a password (min 8 chars)'}
                required
                minLength={mode === 'register' ? 8 : undefined}
              />
            </div>

            {mode === 'register' && (
              <div className="control-group" style={{ marginBottom: '1rem' }}>
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                />
              </div>
            )}

            <MenuButton 
              type="submit" 
              disabled={loading}
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              {loading ? 'Please wait...' : (mode === 'login' ? 'Login' : 'Create Account')}
            </MenuButton>
          </form>

          <div style={{ marginTop: '1rem', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
            {mode === 'login' ? (
              <>
                Don't have an account?{' '}
                <button 
                  onClick={switchMode}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#60b43c', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    padding: 0,
                    font: 'inherit'
                  }}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button 
                  onClick={switchMode}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#60b43c', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    padding: 0,
                    font: 'inherit'
                  }}
                >
                  Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default AuthModal;
